import axios from "axios";
import { useEffect, useState } from "react";
import {
  Linkedin,
  Search,
  Loader2,
  MapPin,
  ExternalLink,
  Sparkles,
  UserPlus,
  Send,
  X,
  ChevronDown,
  ChevronUp,
  Building2,
  Briefcase,
  SlidersHorizontal,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const SourcingLinkedInPage = ({ embedded = false }) => {
  useAuth();
  useDocumentTitle(embedded ? "" : "Sourcing LinkedIn");

  const [accounts, setAccounts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [keywords, setKeywords] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [limit, setLimit] = useState("20");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searching, setSearching] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState("");

  // Add-to-funnel modal
  const [addModal, setAddModal] = useState(null);
  const [addingTo, setAddingTo] = useState("");
  const [adding, setAdding] = useState(false);

  // Invite state
  const [sendingInvite, setSendingInvite] = useState(null); // profile being invited

  // InMail modal
  const [inmailModal, setInmailModal] = useState(null);
  const [inmailJobId, setInmailJobId] = useState("");
  const [generatingInmail, setGeneratingInmail] = useState(false);
  const [inmailSubject, setInmailSubject] = useState("");
  const [inmailBody, setInmailBody] = useState("");
  const [inmailProviderId, setInmailProviderId] = useState("");
  const [sendingInmail, setSendingInmail] = useState(false);
  const [extraInstructions, setExtraInstructions] = useState("");
  const [draftSource, setDraftSource] = useState(""); // "whatsapp_template" | "maya_ai"

  useEffect(() => {
    const load = async () => {
      try {
        const [accRes, jobsRes] = await Promise.all([
          axios.get(`${API}/integrations/synkly/accounts`),
          axios.get(`${API}/integrations/linkedin/sourcing/jobs`),
        ]);
        const connected = (accRes.data || []).filter(
          (a) => a.channel === "linkedin" && (a.status === "connected" || a.status === "active"),
        );
        setAccounts(connected);
        if (connected.length === 1) setSelectedAccount(connected[0].synkly_account_id);
        setJobs(jobsRes.data || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Erro ao carregar dados");
      }
    };
    load();
  }, []);

  const handleSearch = async () => {
    if (!selectedAccount) {
      toast.error("Selecione uma conta LinkedIn conectada.");
      return;
    }
    if (!keywords.trim() && !title.trim()) {
      toast.error("Digite ao menos palavras-chave ou cargo.");
      return;
    }
    setSearching(true);
    setHasSearched(true);
    setProfiles([]);
    setError("");
    try {
      const res = await axios.post(
        `${API}/integrations/linkedin/sourcing/search`,
        {
          linkedin_account_id: selectedAccount,
          keywords: keywords.trim(),
          title: title.trim(),
          company: company.trim(),
          location: location.trim(),
          limit: parseInt(limit, 10) || 20,
        },
      );
      setProfiles(res.data.profiles || []);
      if ((res.data.profiles || []).length === 0) {
        toast.info("Nenhum perfil encontrado. Tente outras palavras-chave.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro na busca");
      toast.error(err?.response?.data?.detail || "Erro na busca");
    } finally {
      setSearching(false);
    }
  };

  const handleOpenAdd = (profile) => {
    setAddingTo("");
    setAddModal(profile);
  };

  const handleConfirmAdd = async () => {
    if (!addingTo || !addModal) return;
    setAdding(true);
    try {
      await axios.post(`${API}/integrations/linkedin/sourcing/add-to-funnel`, {
        linkedin_account_id: selectedAccount,
        job_id: addingTo,
        profile_url: addModal.profile_url,
        name_hint: addModal.name,
        headline_hint: addModal.headline,
      });
      toast.success(
        `${addModal.name} adicionado(a) ao funil. CV sendo gerado em background.`,
      );
      setAddModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao adicionar ao funil");
    } finally {
      setAdding(false);
    }
  };

  const handleSendInvite = async (profile) => {
    if (!selectedAccount) {
      toast.error("Selecione uma conta LinkedIn antes.");
      return;
    }
    if (!profile.provider_id) {
      toast.error("Não foi possível resolver o ID LinkedIn deste perfil. Gere uma mensagem primeiro para obtê-lo.");
      return;
    }
    setSendingInvite(profile.provider_id);
    try {
      await axios.post(`${API}/integrations/linkedin/invites/send`, {
        linkedin_account_id: selectedAccount,
        attendee_provider_id: profile.provider_id,
        attendee_name: profile.name || "",
        attendee_headline: profile.headline || "",
        attendee_profile_url: profile.profile_url || "",
      });
      toast.success(`Convite enviado para ${profile.name}!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar convite");
    } finally {
      setSendingInvite(null);
    }
  };

  const handleOpenInmail = (profile) => {
    setInmailModal(profile);
    setInmailJobId("");
    setInmailSubject("");
    setInmailBody("");
    setInmailProviderId(profile.provider_id || "");
    setExtraInstructions("");
    setDraftSource("");
  };

  const handleGenerateInmail = async () => {
    setGeneratingInmail(true);
    try {
      const res = await axios.post(
        `${API}/integrations/linkedin/messages/draft-inmail`,
        {
          linkedin_account_id: selectedAccount,
          job_id: inmailJobId === "__none__" ? "" : inmailJobId,
          profile_url: inmailModal.profile_url || "",
          attendee_provider_id: inmailModal.provider_id || "",
          candidate_name_hint: inmailModal.name || "",
          extra_instructions: extraInstructions.trim(),
        },
      );
      setInmailSubject(res.data.subject || "");
      setInmailBody(res.data.body || "");
      setInmailProviderId(res.data.provider_id || inmailProviderId);
      setDraftSource(res.data.source || "");
      toast.success(
        res.data.source === "whatsapp_template"
          ? "Mensagem montada com o seu template de WhatsApp da vaga."
          : "Maya gerou a mensagem. Revise e edite antes de enviar.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar mensagem");
    } finally {
      setGeneratingInmail(false);
    }
  };

  const handleSendInmail = async () => {
    if (!inmailBody.trim()) {
      toast.error("Mensagem não pode estar vazia.");
      return;
    }
    if (!inmailProviderId) {
      toast.error("Não consegui resolver o LinkedIn ID do candidato. Gere a mensagem primeiro.");
      return;
    }
    setSendingInmail(true);
    try {
      await axios.post(`${API}/integrations/linkedin/messages/send`, {
        linkedin_account_id: selectedAccount,
        attendee_provider_id: inmailProviderId,
        text: inmailBody,
        job_id: inmailJobId === "__none__" ? "" : inmailJobId,
        candidate_name: inmailModal?.name || "",
      });
      toast.success(`Mensagem enviada para ${inmailModal.name}`);
      setInmailModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar mensagem");
    } finally {
      setSendingInmail(false);
    }
  };

  return (
    <div data-testid="sourcing-linkedin-page" className="space-y-6">
      {/* Header — hidden when used as embedded tab */}
      {!embedded && (
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
            Sourcing LinkedIn
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
            Busca ativa de talentos
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Procure profissionais no LinkedIn usando palavras-chave. Adicione ao
            funil de uma vaga ou envie um InMail personalizado gerado pela Maya.
          </p>
        </div>
      )}

      {/* Search form */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        {/* Row 1 — conta + keywords + buscar */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Conta LinkedIn
            </label>
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger data-testid="sourcing-account-select" className="h-10">
                <SelectValue placeholder="Selecione…" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.synkly_account_id} value={a.synkly_account_id}>
                    {a.label || a.linkedin_name || "(sem nome)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {accounts.length === 0 && (
              <p className="text-[10px] text-amber-700 mt-1">
                Nenhuma conta conectada. Vá em <strong>Configurações → Synkly</strong>.
              </p>
            )}
          </div>
          <div className="md:col-span-7">
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Palavras-chave (skills, tecnologias, nome...)
            </label>
            <Input
              data-testid="sourcing-keywords-input"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder='ex: "Python machine learning" ou "vendas B2B SaaS"'
              className="h-10"
            />
          </div>
          <div className="md:col-span-2">
            <Button
              data-testid="sourcing-search-button"
              onClick={handleSearch}
              disabled={searching || !selectedAccount}
              className="h-10 w-full bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {searching ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Buscando…</>
              ) : (
                <><Search className="w-4 h-4 mr-1.5" />Buscar</>
              )}
            </Button>
          </div>
        </div>

        {/* Toggle filtros avançados */}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition font-medium"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filtros avançados
          {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {(title || company || location || limit !== "20") && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold">
              {[title, company, location, limit !== "20" ? `${limit} resultados` : ""].filter(Boolean).length} ativos
            </span>
          )}
        </button>

        {/* Filtros avançados expandidos */}
        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1 border-t border-slate-100">
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                Cargo / Título
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder='ex: "Gerente de Projetos"'
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                Empresa atual
              </label>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder='ex: "Itaú", "Google"'
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                Localização
              </label>
              <Input
                data-testid="sourcing-location-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="São Paulo, Brasil"
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Nº de resultados
              </label>
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 perfis</SelectItem>
                  <SelectItem value="20">20 perfis</SelectItem>
                  <SelectItem value="30">30 perfis</SelectItem>
                  <SelectItem value="50">50 perfis</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
      </section>

      {/* Results */}
      {hasSearched && !searching && profiles.length === 0 && !error && (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <Linkedin className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600">
            Nenhum perfil encontrado. Tente outras palavras-chave ou ajuste a localização.
          </p>
        </div>
      )}

      {profiles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {profiles.map((p, idx) => (
            <div
              key={p.provider_id || p.profile_url || idx}
              data-testid={`sourcing-result-${idx}`}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-400 transition-colors"
            >
              <div className="flex items-start gap-3 mb-3">
                {p.picture_url ? (
                  <img
                    src={p.picture_url}
                    alt={p.name}
                    className="w-12 h-12 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-50 text-brand-blue flex items-center justify-center shrink-0">
                    <Linkedin className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-slate-900 leading-snug">
                    {(p.profile_url || p.public_id) ? (
                      <a
                        href={p.profile_url || `https://www.linkedin.com/in/${p.public_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-brand-blue hover:underline transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {p.name || "Sem nome"}
                      </a>
                    ) : (
                      p.name || "Sem nome"
                    )}
                  </h3>
                  {p.headline && (
                    <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">
                      {p.headline}
                    </p>
                  )}
                  {p.location && (
                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {p.location}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                <Button
                  data-testid={`sourcing-add-${idx}`}
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenAdd(p)}
                  className="flex-1 h-8 text-xs"
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                  Adicionar ao funil
                </Button>
                <Button
                  data-testid={`sourcing-invite-${idx}`}
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendInvite(p)}
                  disabled={sendingInvite === p.provider_id}
                  className="flex-1 h-8 text-xs text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                >
                  {sendingInvite === p.provider_id ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <UserCheck className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Enviar Convite
                </Button>
                <Button
                  data-testid={`sourcing-inmail-${idx}`}
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenInmail(p)}
                  className="flex-1 h-8 text-xs text-violet-700 hover:bg-violet-50 border-violet-200"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  Mensagem com Maya
                </Button>
                {p.profile_url && (
                  <a
                    href={p.profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-8 px-2 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50"
                    title="Abrir no LinkedIn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add to funnel modal */}
      <Dialog open={!!addModal} onOpenChange={(o) => !o && setAddModal(null)}>
        <DialogContent data-testid="sourcing-add-dialog">
          <DialogHeader>
            <DialogTitle>Adicionar ao funil</DialogTitle>
            <DialogDescription>
              Selecione a vaga onde <strong>{addModal?.name}</strong> entrará.
              O GridRecruiter vai puxar o perfil completo, gerar um CV e rodar a
              IA para pontuar contra os requisitos da vaga (~30s em background).
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Vaga
            </label>
            <Select value={addingTo} onValueChange={setAddingTo}>
              <SelectTrigger data-testid="sourcing-add-job-select">
                <SelectValue placeholder="Selecione uma vaga…" />
              </SelectTrigger>
              <SelectContent>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddModal(null)} disabled={adding}>
              Cancelar
            </Button>
            <Button
              data-testid="sourcing-add-confirm"
              onClick={handleConfirmAdd}
              disabled={!addingTo || adding}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {adding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* InMail modal */}
      <Dialog open={!!inmailModal} onOpenChange={(o) => !o && setInmailModal(null)}>
        <DialogContent
          data-testid="sourcing-inmail-dialog"
          className="max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Mensagem para {inmailModal?.name}
            </DialogTitle>
            <DialogDescription>
              A Maya vai analisar o perfil do(a) candidato(a) + a vaga e gerar
              uma mensagem personalizada. Você revisa e envia via chat do LinkedIn.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Vaga (opcional — melhora a mensagem da Maya)
              </label>
              <Select value={inmailJobId} onValueChange={setInmailJobId}>
                <SelectTrigger data-testid="sourcing-inmail-job-select">
                  <SelectValue placeholder="Selecione uma vaga ou deixe em branco…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhuma vaga (outreach genérico)</SelectItem>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Instruções extras pra Maya (opcional)
              </label>
              <Input
                data-testid="sourcing-inmail-extra-input"
                value={extraInstructions}
                onChange={(e) => setExtraInstructions(e.target.value)}
                placeholder='ex: "Mencionar que trabalho é 100% remoto"'
              />
            </div>
            <Button
              data-testid="sourcing-inmail-generate"
              onClick={handleGenerateInmail}
              disabled={generatingInmail}
              variant="outline"
              className="w-full text-violet-700 border-violet-200 hover:bg-violet-50"
            >
              {generatingInmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {inmailSubject ? "Gerar novamente" : "Gerar com Maya"}
            </Button>

            {inmailSubject && (
              <>
                {draftSource && (
                  <div
                    data-testid="sourcing-inmail-source-hint"
                    className={`text-[11px] rounded-md px-2.5 py-1.5 border ${
                      draftSource === "whatsapp_template"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-violet-50 border-violet-200 text-violet-800"
                    }`}
                  >
                    {draftSource === "whatsapp_template" ? (
                      <>
                        ✅ <strong>Usando seu template de WhatsApp</strong> desta vaga
                        — mesma mensagem nos 2 canais para manter consistência.
                      </>
                    ) : (
                      <>
                        ✨ <strong>Gerado pela Maya com IA</strong> — mensagem via chat
                        do LinkedIn (sem necessidade de créditos de InMail).
                      </>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                    Abertura sugerida
                    <span className="text-slate-400 font-normal ml-1">(referência — não é enviada)</span>
                  </label>
                  <Input
                    data-testid="sourcing-inmail-subject"
                    value={inmailSubject}
                    onChange={(e) => setInmailSubject(e.target.value)}
                    maxLength={200}
                    className="text-slate-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                    Mensagem
                  </label>
                  <Textarea
                    data-testid="sourcing-inmail-body"
                    value={inmailBody}
                    onChange={(e) => setInmailBody(e.target.value)}
                    rows={8}
                    className="font-sans text-sm"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    {inmailBody.length} caracteres · ~
                    {Math.ceil(inmailBody.split(/\s+/).length)} palavras
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setInmailModal(null)}
              disabled={sendingInmail}
            >
              Cancelar
            </Button>
            <Button
              data-testid="sourcing-inmail-send"
              onClick={handleSendInmail}
              disabled={!inmailBody || sendingInmail}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {sendingInmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar mensagem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SourcingLinkedInPage;
